import { yupResolver } from "@hookform/resolvers/yup";
import { ChevronLeftIcon } from "@radix-ui/react-icons";
import { Button, Card, Flex } from "@radix-ui/themes";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { useQuery, useQueryClient } from "react-query";
import { useNavigate } from "react-router-dom";
import * as yup from "yup";
import SelectComponent from "../../components/inputs/Select/SelectComponent";
import TextFieldComponent from "../../components/inputs/Textfield/TextfieldComponent";
import { useGetAcademicYearQuery } from "../../hooks/useGetAcademicYearQuery";
import { useGetClassesQuery } from "../../hooks/useGetClassesQuery";
import { useGetCoursesQuery } from "../../hooks/useGetCoursesQuery";
import { useGetDepartamentsQuery } from "../../hooks/useGetDepartamentsQuery";
import { useGetFacultiesQuery } from "../../hooks/useGetFacultiesQuery";
import { useGetSubjectsQuery } from "../../hooks/useGetSubjectsQuery";
import { routes } from "../../routes";
import { getAssessments } from "../../services/school/assessment/getAssessments";
import { getStudentsAssessments } from "../../services/school/student/getStudentsAssessments";
import { getStudentsFromClass } from "../../services/school/student/getStudentsFromClass";
import { StudentGradesForm } from "./StudentGradesForm";
import { useGetUniversitiesQuery } from "../../hooks/useGetUniversitiesQuery";
import { useGetPlanSubjectsQuery } from "../../hooks/useGetPlanSubjectsQuery";
import { useGetStudentClassesQuery } from "../../hooks/useGetStudentClassesQuery";

export const createStudentAssessmentSchema = yup.object().shape({
  idAcademicYear: yup.string().required("Ano Lectivo é obrigatório"),
  idSubject: yup.string().required("Disciplina é obrigatória"),
  idClass: yup.string().required("Turma é obrigatória"),
  studentAssessments: yup.array().of(
    yup.object().shape({
      idStudent: yup.string().required("ID do estudante é obrigatório"),
      assessments: yup.array().of(
        yup.object().shape({
          idAssessment: yup.string().required("ID da avaliação é obrigatório"),
          grade: yup
            .number()
            .typeError("A nota deve ser um número válido")
            .min(0, "A nota deve ser no mínimo 0")
            .max(20, "A nota deve ser no máximo 20")
            .required("Nota é obrigatória"),
          status: yup.boolean().required("Status é obrigatório"),
          reason: yup.string(),
        })
      ),
    })
  ),
  idUniversity: yup.string().optional(),
  idFaculty: yup.string().required("Unidade orgânica é obrigatória"),
  idDepartament: yup.string().required("Departamento é obrigatório"),
  level: yup.number().required("Ano é obrigatório"),
  semester: yup.number().required("Semestre é obrigatório"),
  idCourse: yup.string().required("Curso é obrigatório"),
});
export const GeneralStudentGrades = () => {
  const navigate = useNavigate();

  const {
    control,
    setValue,
    getValues,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(createStudentAssessmentSchema),
    defaultValues: {
      idAcademicYear: "",
      idClass: "",
      idSubject: "",
      studentAssessments: [],
      idFaculty: "",
      idUniversity: "",
      level: 0,
      semester: 0,
      idCourse: "",
    },
    mode: "onBlur",
    reValidateMode: "onBlur",
    shouldUseNativeValidation: false,
  });

  const {
    data: AcademicYears,
    isLoading: isLoadingAcademicYears,
    isError: errorAcademicYears,
  } = useGetAcademicYearQuery({ PageNumber: 1, PageSize: 400 });
  const {
    data: universities,
    isLoading: isLoadingUniversities,
    isError: errorUniversities,
    refetch: refetchUniversities,
  } = useGetUniversitiesQuery({ PageNumber: 1, PageSize: 400 });
  const {
    data: faculties,
    isLoading: isLoadingFaculties,
    isError: errorFaculties,
    refetch: refetchFaculties,
  } = useGetFacultiesQuery({
    PageNumber: 1,
    PageSize: 400,
    IdUniversity: getValues("idUniversity"),
  });
  const {
    data: departaments,
    isLoading: isLoadingDepartaments,
    isError: errorDepartaments,
    refetch: refreshDepartaments,
  } = useGetDepartamentsQuery(
    { PageNumber: 1, PageSize: 400, IdFaculty: getValues("idFaculty") },
    { enabled: !!getValues("idFaculty") }
  );
  const {
    data: courses,
    isLoading: isLoadingCourses,
    isError: errorCourses,
    refetch: refreshCourses,
  } = useGetCoursesQuery(
    { PageNumber: 1, PageSize: 400, IdDepartament: getValues("idDepartament") },
    { enabled: !!getValues("idDepartament") }
  );

  const {
    isLoading: isLoadingClasses,
    refetch: fetchClasses,
    data: classes,
    isError: isErrorClasses,
  } = useGetClassesQuery({
    PageNumber: 1,
    PageSize: 400,
    IdAcademicYear: getValues("idAcademicYear"),
    Semester: getValues("semester"),
    Level: getValues("level"),
    IdCourse: getValues("idCourse"),
  });

  const {
    data: subjects,
    isLoading: isLoadingSubjects,
    isError: errorSubjects,
    refetch: refreshSubjects,
  } = useGetPlanSubjectsQuery({ PageNumber: 1, PageSize: 400,   IdAcademicYear: getValues("idAcademicYear"),
    Semester: getValues("semester"),
    Level: getValues("level"),
    IdCourse: getValues("idCourse")}, {
      enabled: !!getValues('semester') && !!getValues('level') && !!getValues('idCourse') && !!getValues('idAcademicYear') && !!getValues('idClass'),
    });

  const {
    isLoading: isLoadingAssessments,
    refetch: fetchAssessments,
    data: assessments,
    isError: isErrorAssessments,
  } = useQuery(
    ["fetchAssessments"],
    async () => {
      try {
        const assessments = await getAssessments({
          PageNumber: 1,
          PageSize: 400,
          IdClass: getValues("idClass"),
          IdSubject: getValues("idSubject"),
        });
        return assessments;
      } catch (error) {}
    },
    {
      enabled: false,
      refetchOnWindowFocus: false,
      retry: false,
    }
  );

   const { data: students, status: statusStudentsClass, isLoading: isLoadingStudentsFromClass, refetch: fetchStudentsFromClass,isError: isErrorStudentsFromClass } = useGetStudentClassesQuery({
      PageSize: 400,
      PageNumber: 1,
      IdClass: getValues('idClass'),
      IdSubject: getValues('idSubject')
    },
  {
      enabled: !!getValues('idClass') && !!getValues('idSubject')
      ,
      refetchOnWindowFocus: true,
      retry: true
  });

  // const { isLoading: isLoadingStudentsFromClass, refetch: fetchStudentsFromClass, data: students, isError: isErrorStudentsFromClass } = useQuery(
  //   ['fetchStudentsFromClass'],
  //   async () => {
  //     try {
  //       const students = await getStudentsFromClass({ 
  //         PageNumber: 1, PageSize: 1000, 
  //         IdClass: getValues('idClass'),
  //         IdSubject: getValues('idSubject')
  //       });
  //       return students;
  //     } catch (error) {
  //     }
  //   },
  //   {
  //     enabled: !!getValues('idClass') && !!getValues('idSubject'),
  //     refetchOnWindowFocus: false,
  //     retry: false
  //   }
  // );

  const { isLoading: isLoadingStudentAssessments, refetch: fetchStudentAssessments, data: studentAssessments, isError: isErrorStudentAssessments } = useQuery(
    ['fetchStudentAssessments'],
    async () => {
      try {
        const studentsAssessments = await getStudentsAssessments({ PageNumber: 1, PageSize: 1000, IdClass: getValues('idClass'), IdSubject: getValues('idSubject'),});
        return studentsAssessments;
      } catch (error) {
      }
    },
    {
      enabled: false,
      refetchOnWindowFocus: false,
      retry: false
    }
  );

  const mapStudentsAssessments = (idStudent?: string) => {
    const existingStudentAssessments = studentAssessments?.items.filter(sa => sa.idStudent === idStudent);
    return assessments?.items.map(assessment => {
      const existingAssessment = existingStudentAssessments?.find(sa => sa.idAssessment === assessment.id);
      const _assessment =
        existingAssessment ? {
          ...existingAssessment,
        } : {
          idAssessment: assessment?.id!,
          grade: 0,
          status: true,
          reason: '',
        };
      return _assessment;
    }) || [];
  };

  useEffect(() => {
    if (students?.items) {
      setValue('studentAssessments', [...students.items].sort((a, b) =>
        (a.student || "").localeCompare(b.student || "")
      ).map((student) => ({
        idStudent: student.idStudent,
        assessments: mapStudentsAssessments(student?.idStudent),
      })));
    }
  }, [students?.items, assessments?.items, studentAssessments?.items, setValue]);

  const handleChangeClass = (idClass: string) => {
    setValue("idClass", idClass);
    refreshSubjects();
  };

  const handleChangeSubject = (idSubject: string) => {
    setValue("idSubject", idSubject);
    fetchAssessments();
    fetchStudentsFromClass();
    fetchStudentAssessments();
  };

  const handleBack = () => {
    navigate(routes.dashboard);
  };

  const handleFacultyChange = (idFaculty: string) => {
    setValue("idFaculty", idFaculty);
    refreshDepartaments();
  };

  const handleDepartamentChange = (idDepartament: string) => {
    setValue("idDepartament", idDepartament);
    refreshCourses();
  };

  const handleCourseChange = (idCourse: string) => {
    setValue("idCourse", idCourse);
    fetchStudentsFromClass();
  };

  const handleFullnameChange = (e: any) => {
    const level = e.target.value;
    setValue("level", level);
    setValue("idFaculty", "");
    setValue("idDepartament", "");
    setValue("idCourse", "");
    setValue("idClass", "");
    setValue("idSubject", "");
    // fetchAssessments();
    // fetchStudentsFromClass();
    // fetchStudentAssessments();
  };
  const handleUniversityChange = (id: string) => {
    setValue("idUniversity", id);
    setValue("idFaculty", "");
    setValue("idDepartament", "");
    refetchFaculties();
  };
  return (
    <Card>
      <Flex direction="column" gap="4">
        <Flex gap="4" direction="column">
          <Card size="1">
            <Flex direction="column" gap="4">
              <Flex gap="3">
                <SelectComponent
                  name="idAcademicYear"
                  label="Ano lectivo"
                  control={control}
                  errors={errors}
                  style={{ width: "100%" }}
                  options={AcademicYears?.items.map((academicYear) => ({
                    value: academicYear.id ?? "",
                    label: academicYear.year ?? "",
                  }))}
                  onSelect={handleFacultyChange}
                />

                <TextFieldComponent
                  name="level"
                  label="Ano"
                  control={control}
                  errors={errors}
                  fieldProps={{
                    placeholder: "Ano",
                    onChange: handleFullnameChange,
                  }}
                  style={{ width: "100%" }}
                />
                <TextFieldComponent
                  name="semester"
                  label="Semestre"
                  control={control}
                  errors={errors}
                  fieldProps={{ placeholder: "Semestre" }}
                  style={{ width: "100%" }}
                />
              </Flex>
              <Flex gap="3">
                <SelectComponent
                  name="idUniversity"
                  label="Instituição de Ensino Superior"
                  control={control}
                  errors={errors}
                  style={{ width: "100%" }}
                  options={universities?.items.map((u) => ({
                    value: u.id ?? "",
                    label: u.description ?? "",
                  }))}
                  itemsLoading={isLoadingUniversities}
                  itemsError={errorUniversities}
                  onSelect={handleUniversityChange}
                  required={true}
                />
                <SelectComponent
                  name="idFaculty"
                  label="Unidade orgânica"
                  control={control}
                  errors={errors}
                  style={{ width: "100%" }}
                  options={faculties?.items.map((faculty) => ({
                    value: faculty.id ?? "",
                    label: faculty.description ?? "",
                  }))}
                  onSelect={handleFacultyChange}
                />
                <SelectComponent
                  name="idDepartament"
                  label="Departamento"
                  control={control}
                  errors={errors}
                  style={{ width: "100%" }}
                  options={departaments?.items.map((departament) => ({
                    value: departament.id ?? "",
                    label: departament.description ?? "",
                  }))}
                  onSelect={handleDepartamentChange}
                />
              </Flex>
              <Flex gap="3">
                <SelectComponent
                  name="idCourse"
                  label="Curso"
                  control={control}
                  errors={errors}
                  style={{ width: "100%" }}
                  options={courses?.items.map((course) => ({
                    value: course.id ?? "",
                    label: course.name ?? "",
                  }))}
                  onSelect={handleCourseChange}
                />

                <SelectComponent
                  name="idClass"
                  label="Turma"
                  control={control}
                  errors={errors}
                  style={{ width: "100%" }}
                  options={classes?.items.map((_class) => {
                    return {
                      label: _class.description,
                      value: _class.id ?? "",
                    };
                  })}
                  onSelect={handleChangeClass}
                  itemsLoading={isLoadingClasses}
                  itemsError={isErrorClasses}
                />

                <SelectComponent
                  name="idSubject"
                  label="Disciplina"
                  control={control}
                  errors={errors}
                  style={{ width: "100%" }}
                  options={subjects?.items.map((subject) => {
                    return {
                      label: subject.subject??"",
                      value: subject.idSubject ?? "",
                    };
                  })}
                  itemsLoading={isLoadingSubjects}
                  itemsError={errorSubjects}
                  onSelect={handleChangeSubject}
                />
              </Flex>
            </Flex>
          </Card>

          <StudentGradesForm
            students={students?.items || []}
            assessments={assessments?.items || []}
            control={control}
            errors={errors}
            readonly={true}
          />

          <Flex direction="row" gap="4" style={{ alignSelf: "end" }}>
            <Button
              color="red"
              onClick={() => {
                handleBack();
              }}
            >
              <ChevronLeftIcon /> Voltar
            </Button>
          </Flex>
        </Flex>
      </Flex>
    </Card>
  );
};
